/* Smooth WebGL capsule rendering; the existing image remains the no-JS fallback. */
(() => {
  const vertexSource = `
    attribute vec2 aPosition;
    void main() { gl_Position = vec4(aPosition, 0.0, 1.0); }
  `;

  const fragmentSource = `
    precision highp float;
    uniform vec2 uResolution;
    uniform vec3 uAxis;

    float capsuleDistance(vec3 point, vec3 axis) {
      float alongAxis = dot(point, axis);
      float center = clamp(alongAxis, -0.95, 0.95);
      float shellRadius = mix(0.515, 0.54, smoothstep(0.012, 0.052, center));
      if (alongAxis < -0.95) return length(point + axis * 0.95) - 0.515;
      if (alongAxis > 0.95) return length(point - axis * 0.95) - 0.54;
      return length(point - axis * center) - shellRadius;
    }

    vec3 surfaceNormal(vec3 point, vec3 axis) {
      const float offset = 0.002;
      vec2 step = vec2(offset, 0.0);
      return normalize(vec3(
        capsuleDistance(point + step.xyy, axis) - capsuleDistance(point - step.xyy, axis),
        capsuleDistance(point + step.yxy, axis) - capsuleDistance(point - step.yxy, axis),
        capsuleDistance(point + step.yyx, axis) - capsuleDistance(point - step.yyx, axis)
      ));
    }

    void main() {
      vec2 uv = (gl_FragCoord.xy * 2.0 - uResolution) / min(uResolution.x, uResolution.y);
      uv *= 1.72;
      vec3 rayOrigin = vec3(uv, 3.2);
      vec3 rayDirection = vec3(0.0, 0.0, -1.0);
      float distanceAlongRay = 0.0;
      bool hit = false;
      for (int stepIndex = 0; stepIndex < 64; stepIndex++) {
        vec3 point = rayOrigin + rayDirection * distanceAlongRay;
        float distanceToSurface = capsuleDistance(point, uAxis);
        if (distanceToSurface < 0.0015) { hit = true; break; }
        if (distanceAlongRay > 6.4) break;
        distanceAlongRay += max(distanceToSurface * 0.72, 0.004);
      }
      if (!hit) { gl_FragColor = vec4(0.0); return; }

      vec3 point = rayOrigin + rayDirection * distanceAlongRay;
      vec3 normal = surfaceNormal(point, uAxis);
      vec3 lightDirection = normalize(vec3(-0.45, 0.62, 0.85));
      vec3 viewDirection = -rayDirection;
      float diffuse = max(dot(normal, lightDirection), 0.0);
      float softFill = max(dot(normal, normalize(vec3(0.7, -0.45, 0.35))), 0.0);
      float reflection = max(dot(reflect(-lightDirection, normal), viewDirection), 0.0);
      float broadGloss = pow(reflection, 7.0);
      float softGloss = pow(reflection, 22.0);
      float rim = pow(1.0 - max(dot(normal, viewDirection), 0.0), 2.4);

      float alongAxis = dot(point, uAxis);
      float capBlend = smoothstep(0.012, 0.06, alongAxis);
      vec3 bodyShell = vec3(0.945, 0.947, 0.935);
      vec3 capShell = vec3(0.99, 0.985, 0.965);
      vec3 shell = mix(bodyShell, capShell, capBlend);
      vec3 color = shell * (0.78 + diffuse * 0.19 + softFill * 0.055);
      color += vec3(1.0, 0.995, 0.97) * (broadGloss * 0.075 + softGloss * 0.055);
      color += vec3(0.76, 0.84, 0.88) * rim * 0.055;

      float seam = 1.0 - smoothstep(0.014, 0.042, abs(alongAxis - 0.035));
      color *= 1.0 - seam * 0.075;
      color += seam * broadGloss * 0.025;
      gl_FragColor = vec4(color, 1.0);
    }
  `;

  function makeShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
  }

  document.querySelectorAll('.hip-capsule-visual').forEach(figure => {
    const canvas = figure.querySelector('.hip-capsule-canvas');
    const stage = figure.querySelector('.hip-capsule-stage');
    const gl = canvas.getContext('webgl', { alpha: true, antialias: true });
    if (!gl) return;
    const vertex = makeShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragment = makeShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    if (!vertex || !fragment) return;

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    gl.useProgram(program);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    const resolution = gl.getUniformLocation(program, 'uResolution');
    const axisUniform = gl.getUniformLocation(program, 'uAxis');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const interactiveMedia = matchMedia('(min-width: 800px) and (hover: hover) and (pointer: fine)');
    let visible = false;
    let elapsed = 0;
    let lastFrame = 0;
    let animationFrame = 0;
    let azimuthOffset = 0;
    let elevationOffset = 0;
    let dragging = false;
    let activePointer = null;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragAzimuth = 0;
    let dragElevation = 0;

    function resize() {
      const size = stage.clientWidth;
      const density = Math.min(devicePixelRatio || 1, 2);
      const pixels = Math.max(1, Math.round(size * density));
      if (canvas.width !== pixels) { canvas.width = pixels; canvas.height = pixels; }
      gl.viewport(0, 0, canvas.width, canvas.height);
    }

    function automaticAngles() {
      return [
        elapsed / 58000 * Math.PI * 2,
        0.42 + Math.sin(elapsed / 21000 * Math.PI * 2) * 0.58
      ];
    }

    function displayedAngles() {
      if (dragging) return [dragAzimuth, dragElevation];
      const automatic = automaticAngles();
      return [automatic[0] + azimuthOffset, automatic[1] + elevationOffset];
    }

    function draw() {
      resize();
      const angles = displayedAngles();
      const azimuth = angles[0];
      const elevation = Math.max(-1.35, Math.min(1.35, angles[1]));
      const axis = [
        Math.cos(elevation) * Math.cos(azimuth),
        Math.sin(elevation),
        Math.cos(elevation) * Math.sin(azimuth)
      ];
      gl.uniform2f(resolution, canvas.width, canvas.height);
      gl.uniform3fv(axisUniform, axis);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      canvas.style.setProperty('--capsule-float', `${Math.sin(elapsed / 5200) * 4}px`);
    }

    function animate(now) {
      animationFrame = 0;
      if (!visible || reducedMotion.matches) return;
      elapsed += lastFrame ? Math.min(now - lastFrame, 50) : 0;
      lastFrame = now;
      draw();
      animationFrame = requestAnimationFrame(animate);
    }

    function update() {
      cancelAnimationFrame(animationFrame);
      lastFrame = 0;
      draw();
      if (visible && !reducedMotion.matches) animationFrame = requestAnimationFrame(animate);
    }

    function setInteractiveState() {
      const enabled = interactiveMedia.matches;
      stage.classList.toggle('is-interactive', enabled);
      if (enabled) {
        stage.tabIndex = 0;
        stage.setAttribute('role', 'img');
        stage.setAttribute('aria-label', stage.dataset.interactiveLabel);
      } else {
        stage.removeAttribute('tabindex');
        stage.removeAttribute('role');
        stage.removeAttribute('aria-label');
        dragging = false;
        activePointer = null;
        stage.classList.remove('is-dragging');
      }
    }

    function beginDrag(event) {
      if (!interactiveMedia.matches || event.button !== 0) return;
      const angles = displayedAngles();
      dragging = true;
      activePointer = event.pointerId;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragAzimuth = angles[0];
      dragElevation = angles[1];
      stage.classList.add('is-dragging');
      stage.setPointerCapture(event.pointerId);
      event.preventDefault();
      draw();
    }

    function moveDrag(event) {
      if (!dragging || event.pointerId !== activePointer) return;
      dragAzimuth += (event.clientX - dragStartX) * 0.012;
      dragElevation = Math.max(-1.35, Math.min(1.35, dragElevation - (event.clientY - dragStartY) * 0.01));
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      event.preventDefault();
      draw();
    }

    function finishDrag(event) {
      if (!dragging || event.pointerId !== activePointer) return;
      const automatic = automaticAngles();
      azimuthOffset = dragAzimuth - automatic[0];
      elevationOffset = dragElevation - automatic[1];
      dragging = false;
      activePointer = null;
      stage.classList.remove('is-dragging');
      if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
      draw();
    }

    function nudgeWithKeyboard(event) {
      if (!interactiveMedia.matches || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') azimuthOffset -= 0.16;
      if (event.key === 'ArrowRight') azimuthOffset += 0.16;
      if (event.key === 'ArrowUp') elevationOffset = Math.min(1.1, elevationOffset + 0.12);
      if (event.key === 'ArrowDown') elevationOffset = Math.max(-1.1, elevationOffset - 0.12);
      draw();
    }

    canvas.hidden = false;
    figure.querySelector('img').hidden = true;
    reducedMotion.addEventListener('change', update);
    interactiveMedia.addEventListener('change', setInteractiveState);
    stage.addEventListener('pointerdown', beginDrag);
    stage.addEventListener('pointermove', moveDrag);
    stage.addEventListener('pointerup', finishDrag);
    stage.addEventListener('pointercancel', finishDrag);
    stage.addEventListener('keydown', nudgeWithKeyboard);
    new ResizeObserver(draw).observe(stage);
    new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      update();
    }, { threshold: 0.2 }).observe(figure.closest('.hip-capsule'));
    document.addEventListener('visibilitychange', () => {
      const box = stage.getBoundingClientRect();
      visible = !document.hidden && box.bottom > 0 && box.top < innerHeight;
      update();
    });
    setInteractiveState();
    update();
  });
})();
